import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { FaFilter, FaUndo } from 'react-icons/fa'
import ReportProblemOutlinedIcon from '@mui/icons-material/ReportProblemOutlined'
import PageHeader from '../../shared/PageHeader'
import AppButton from '../../shared/AppButton'
import AppTable from '../../shared/AppTable'
import CustomFilterInput from '../../shared/CustomFilterInput'
import { API_BASE_URL } from '../../config/apiConfig'
import '../Beneficiary/styles/BeneficiaryList.css'

const getMonthDateRange = () => {
  const today = new Date()
  const firstDay = new Date(today.getFullYear(), today.getMonth(), 1)
  const lastDay = new Date(today.getFullYear(), today.getMonth() + 1, 0)

  return {
    from_date: formatDate(firstDay),
    to_date: formatDate(lastDay)
  }
}

const formatDate = date => {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

const getAuthHeaders = () => {
  const token = localStorage.getItem('token')?.replaceAll('"', '')
  return token ? { Authorization: `Bearer ${token}` } : {}
}

const getPayerOptions = () => {
  try {
    const payersData = JSON.parse(localStorage.getItem('payersData') || '[]')
    return Array.isArray(payersData) ? payersData : []
  } catch (error) {
    return []
  }
}

function DenialManagement() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const defaultRange = useMemo(() => getMonthDateRange(), [])
  const payerOptions = useMemo(() => getPayerOptions(), [])
  const initialFilters = location.state?.denialFilters || {
    from_date: defaultRange.from_date,
    to_date: defaultRange.to_date,
    payer_id: ''
  }
  const [filters, setFilters] = useState({
    from_date: initialFilters.from_date,
    to_date: initialFilters.to_date,
    payer_id: initialFilters.payer_id || ''
  })
  const [appliedFilters, setAppliedFilters] = useState(filters)
  const [loading, setLoading] = useState(false)
  const [denialData, setDenialData] = useState({
    total_rejected_claims: 0,
    total_reasons: 0,
    data: []
  })

  const loadDenials = useCallback(async () => {
    if (!appliedFilters.from_date || !appliedFilters.to_date) {
      toast.error(t('From Date and To Date are required.'))
      return
    }

    setLoading(true)
    try {
      const response = await axios.post(
        `${API_BASE_URL}/claim/denial-management`,
        {
          from_date: appliedFilters.from_date,
          to_date: appliedFilters.to_date,
          payer_id: appliedFilters.payer_id || undefined
        },
        { headers: getAuthHeaders() }
      )

      setDenialData(
        response?.data?.data || {
          total_rejected_claims: 0,
          total_reasons: 0,
          data: []
        }
      )
    } catch (error) {
      toast.error(error?.response?.data?.error || t('Unable to load denials.'))
    } finally {
      setLoading(false)
    }
  }, [appliedFilters, t])

  useEffect(() => {
    loadDenials()
  }, [loadDenials])

  const handleFilterChange = event => {
    const { name, value } = event.target
    setFilters(prev => ({ ...prev, [name]: value }))
  }

  const applyFilters = () => {
    setAppliedFilters(filters)
  }

  const resetFilters = () => {
    const resetValues = {
      from_date: defaultRange.from_date,
      to_date: defaultRange.to_date,
      payer_id: ''
    }
    setFilters(resetValues)
    setAppliedFilters(resetValues)
  }

  const openClaimDetails = row => {
    navigate('/claims/details', {
      state: {
        currentItem: row,
        returnTo: '/claims/denial/management',
        returnState: {
          denialFilters: appliedFilters
        },
        claimsListReturnState: {
          claimsFilters: appliedFilters,
          activeTab: 'rejected'
        }
      }
    })
  }

  return (
    <div className='claimsSubmission_container'>
      <div className='claimsSubmission_page'>
        <PageHeader
          title={t('Denial Management')}
          tooltip={t('Rejected claims grouped by month, payer and rejection reason')}
          subtitle
        />

        <div className='mt-2 w-full'>
          <div className='grid grid-cols-2 gap-x-1.5 gap-y-1.5 pb-2 md:grid-cols-4 lg:grid-cols-7'>
            <CustomFilterInput
              label={t('Submission Date')}
              name='from_date'
              type='date'
              value={filters.from_date}
              onChange={handleFilterChange}
              placeholder={t('From Date')}
            />
            <CustomFilterInput
              label={t('To Date')}
              name='to_date'
              type='date'
              value={filters.to_date}
              onChange={handleFilterChange}
              placeholder={t('To Date')}
            />
            <CustomFilterInput
              label={t('Select Payer')}
              name='payer_id'
              value={filters.payer_id}
              onChange={handleFilterChange}
              placeholder={t('Select Payer')}
              isSelect
              renderOptions={() =>
                payerOptions.map(category => (
                  <optgroup
                    key={category.id}
                    label={
                      category?.name_en
                        ? `${category.name_en} (${category.license_id})`
                        : ''
                    }
                  >
                    {category.payers?.map(payer => (
                      <option key={payer.id} value={payer.id}>
                        {payer.name_en} ({payer.license_id})
                      </option>
                    ))}
                  </optgroup>
                ))
              }
            />
            <div className='col-span-2 flex items-end gap-2 md:col-span-1'>
              <AppButton
                title={t('Apply Filter')}
                onClick={applyFilters}
                disabled={loading}
                isLoading={loading}
                startIcon={<FaFilter className='!text-[12px]' />}
              />
              <AppButton
                title={t('Reset')}
                onClick={resetFilters}
                disabled={loading}
                variant='reset'
                startIcon={<FaUndo className='!text-[12px]' />}
                className='hover:!bg-slate-100'
              />
              {/* Resubmission will be enabled after the denial review flow is finalized. */}
            </div>
          </div>
        </div>

        <div className='mt-3 space-y-3'>
          {loading ? (
            <div className='rounded-lg border border-slate-200 bg-white p-6 text-center text-sm text-slate-500'>
              {t('Loading denial management data...')}
            </div>
          ) : denialData.data.length ? (
            denialData.data.map(group => (
              <DenialGroup
                key={`${group.month}-${group.payer_name}`}
                group={group}
                t={t}
                onView={openClaimDetails}
              />
            ))
          ) : (
            <div className='rounded-lg border border-slate-200 bg-white p-8 text-center'>
              <ReportProblemOutlinedIcon className='text-slate-300' fontSize='large' />
              <p className='mt-2 text-sm font-semibold text-slate-700'>
                {t('No rejected claims found.')}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

const DenialGroup = ({ group, t, onView }) => {
  const rows = group.rejection_reasons.flatMap(reason =>
    reason.claims.map(claim => ({
      ...claim,
      reason: reason.reason,
      reason_code: reason.reason_code,
      claim_count: reason.claim_count
    }))
  )

  const columns = [
    {
      header: t('Reason'),
      field: 'reason',
      width: '260px',
      render: row => (
        <div className='max-w-[320px] whitespace-normal py-1'>
          <p className='text-[13px] font-semibold leading-4 text-slate-800'>
            {row.reason}
          </p>
          {row.reason_code ? (
            <p className='mt-0.5 text-[11px] font-semibold text-slate-400'>
              {row.reason_code}
            </p>
          ) : null}
        </div>
      )
    },
    {
      header: t('Transaction ID'),
      field: 'transaction_id',
      width: '160px',
      render: row => (
        <span className='font-semibold text-slate-800'>{row.transaction_id}</span>
      )
    },
    { header: t('Claim Date'), field: 'claim_date', width: '105px' },
    {
      header: t('Claim Type'),
      field: 'claim_type',
      width: '115px',
      render: row => (
        <span className='capitalize'>{String(row.claim_type || '').toLowerCase()}</span>
      )
    },
    { header: t('Patient Name'), field: 'patient_name', width: '160px' },
    {
      header: t('Patient Document ID'),
      field: 'patient_document_id',
      width: '120px'
    },
    {
      header: t('Member Card ID'),
      field: 'member_card_id',
      width: '130px'
    },
    {
      header: t('Status'),
      field: 'status',
      width: '90px',
      render: row => (
        <span className='rounded bg-red-50 px-2 py-1 text-xs font-bold text-red-600'>
          {row.status}
        </span>
      )
    }
  ]

  return (
    <div className='rounded-lg border border-slate-200 bg-white'>
      <div className='flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 px-3 py-2'>
        <div>
          <p className='text-xs font-bold text-primary'>{group.month}</p>
          <h3 className='text-base font-bold leading-5 text-slate-900'>
            {group.payer_name}
          </h3>
        </div>
      </div>
      <div className='p-3'>
        <AppTable
          columns={columns}
          data={rows}
          loading={false}
          actions
          onView={onView}
          bodyRowPadding='3px'
          containerClassName='custom-table-scrollbar max-h-[420px]'
        />
      </div>
    </div>
  )
}

export default DenialManagement
